'use client';

import type { ReactNode } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { AccessGuard } from '@ps/features/access-control/components/access-guard/access-guard';
import type {
  BioAccessGuard,
  LeadCaptureConfig,
} from '@ps/features/access-control/components/access-guard/guard-descriptor';
import { useLeadFormGuardContext } from '@ps/features/access-control/components/access-guard/lead-form-guard-context';
import { downgradeIfTierHeld } from '@ps/features/access-control/functions/downgradeIfTierHeld';
import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import type {
  PopStoreExtendedSubscriptionLevel,
  PopStoreSubscriptionLevel,
} from '@ps/models';
import type { CreatorInfo } from '@ps/types/creator';

import { useBioUserAccess } from '../../../contexts/user-access/useBioUserAccess';
import type { CustomButtonGuard } from '../../functions/restrictAccessToGuard';

interface Props {
  /** Access requirement from `restrictAccessToGuard(button.restrictAccess)`. */
  guard: CustomButtonGuard;
  /**
   * The page's bio context ({@link resolveBioContext}) — always present (the
   * dispatch context establishes it on both the visitor host and the owner
   * editor). The creator the guard gates against is `bioContext.creatorId`, and
   * it's the subscribe-picker redirect target for a tier gate. Built through
   * {@link bioUrl} so it soft-navs to the `@modal/(.)subscribe` intercept (and
   * carries the `~` sentinel on an independent custom domain).
   */
  bioContext: BioContext;
  /** Ordered lead-form triggers (first match wins). */
  triggers: Array<{ type: string; id: string }>;
  /** Seller identity the guard modals render. */
  sellerProfile: CreatorInfo;
  /**
   * Navigation target once access is granted. Renders an `<a>` leaf (crawlable,
   * modified-click-friendly). Omit for an action button whose whole effect is
   * the guard itself (see {@link actionOnly}).
   */
  href?: string;
  /**
   * The button's effect IS the guard modal — no navigation, no post-grant
   * action (FollowMe: `FollowGuardModal` performs the follow on confirm). Renders
   * a `<button>` leaf that just runs the guard. A serializable boolean (not a
   * callback) so this stays passable from the server-rendered registry to this
   * client component — React forbids passing functions across that boundary.
   */
  actionOnly?: boolean;
  target?: string;
  rel?: string;
  ariaLabel?: string;
  /** Following isn't a meaningful gate (RealEstate) — lead-form only. */
  skipFollowGuard?: boolean;
  /**
   * Creator's standalone lead-capture config, forwarded to the contact-info
   * form so its title/subtitle + field/consent visibility match the creator's
   * setup. Only meaningful for a gate that collects contact info: a
   * `contact-info` guard, or the anonymous path of a `follow` one
   * (`anonymousContactInfo`).
   */
  leadCapture?: LeadCaptureConfig;
  /**
   * Lead attribution for a captured contact (RN `sourceType`/`sourceId`) —
   * which button generated the lead. Forwarded to the guest-contact form when a
   * gate collects contact info (a link button's anonymous "collect contact
   * info" path, `LINK_BUTTON`). Absent → the modal's legacy Calendar/storeId
   * default.
   */
  source?: { type: string; id: string };
  className?: string;
  /** The button's inner content (view body, no link chrome). */
  children: ReactNode;
}

/**
 * The gated link for a navigating custom button (DigitalProduct, RealEstate,
 * Music). A direct mirror of listing-page-builder's `WiredBookButton` — the one
 * in-production-proven `AccessGuard` consumer — adapted from a "Book" button to
 * an `<a>`.
 *
 * A lead form attached to a button gates it **independent of `restrictAccess`**
 * (RN `useCustomButtonAccessGuardInfo` checks the form trigger first, regardless
 * of the subscription restriction). So the gate decision is NOT just the
 * descriptor:
 *
 *  - no restriction AND no triggers → a plain `<a href>` (no guard JS,
 *    crawlable) — the truly-ungated common case.
 *  - otherwise → wrap in `AccessGuard`. `skipFollowGuard` is derived so an
 *    unrestricted button gates ONLY on an attached lead form (the guard proceeds
 *    instantly when no form matches), while a FOLLOWER-restricted button also
 *    gates on follow. (Subscription-tier redirect is a later slice; for now a
 *    subscription restriction gates lead-form-only like `none`, never silently
 *    opening.) The click is intercepted and replays navigation on
 *    `onAccessGranted`.
 */
export function WiredCustomButtonLink({
  guard,
  bioContext,
  triggers,
  sellerProfile,
  href,
  actionOnly,
  target,
  rel,
  ariaLabel,
  skipFollowGuard,
  leadCapture,
  source,
  className,
  children,
}: Props) {
  const router = useRouter();
  // The creator the guard gates against — the same entity the bio is scoped to.
  const storeId = bioContext.creatorId;
  // Follow status comes from the bio's `BioUserAccessProvider` (lazy, opt-in
  // realtime) — passed to `AccessGuard` so the guard stays firestore-free on the
  // visitor path. With realtime off (the default) the context won't otherwise
  // learn about a follow the guard just performed, so on a persisted follow we
  // flip it optimistically (`updateFollowingStatus`) AND re-check the server
  // (`refreshFollowingStatus`) — covering both the immediate and post-login
  // paths, so the button reflects the follow without a manual reload.
  const {
    extendedSubscriptionLevel,
    isFollowing,
    loading: accessLoading,
    updateFollowingStatus,
    refreshFollowingStatus,
  } = useBioUserAccess();
  const { getGuard, isLoaded: leadFormsLoaded } = useLeadFormGuardContext();

  const onFollowPersisted = () => {
    updateFollowingStatus(true);
    refreshFollowingStatus();
  };

  // The button is gated only when there's a REAL gate: a `restrictAccess`
  // descriptor (follow / subscription), a `contact-info` requirement, OR a lead
  // form that actually matches these triggers. Having triggers is not enough —
  // they're just candidates to check. A button with no restriction, no
  // contact-info, and no matching form is ungated (RN: `guard == null` → access
  // granted, no modal) and renders a bare crawlable anchor for guest AND
  // logged-in alike.
  //
  // Render the bare anchor ONLY once we're sure it's ungated:
  //  - any non-`none` guard kind → always gated.
  //  - candidate triggers present but forms not loaded yet → treat as gated
  //    (route through the guard, which has its own readiness) so we never flash
  //    a bare anchor and then discover a form.
  //  - forms loaded + no match (or no triggers at all) → truly ungated.
  const definitelyUngated =
    guard.kind === 'none' &&
    (triggers.length === 0 || (leadFormsLoaded && getGuard(triggers) == null));

  if (definitelyUngated) {
    // Ungated: no guard JS. An `actionOnly` button whose only effect was the
    // (now-absent) gate has nothing to do — render an inert `<button>`.
    if (actionOnly) {
      return (
        <button type="button" aria-label={ariaLabel} className={className}>
          {children}
        </button>
      );
    }
    // An INTERNAL target (bio-scoped `/…`, not opening a new tab) uses
    // `next/link` for a CLIENT transition, so the bio's `@modal/(.)*` intercepts
    // fire (e.g. ContactCard overlays `/contact` as a modal on the live bio
    // instead of hard-loading). `next/link` still emits a crawlable `<a href>`
    // and stays a server-compatible import (no bundle cost). External / new-tab
    // targets (Music, external Text links) stay a plain `<a>`.
    if (href != null && target !== '_blank' && href.startsWith('/')) {
      return (
        <Link
          href={href}
          aria-label={ariaLabel}
          className={className}
          rel={rel}
        >
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        aria-label={ariaLabel}
        className={className}
      >
        {children}
      </a>
    );
  }

  // Run the button's activation once access is granted. A `follow`-kind button
  // has no post-grant action — the modal performed the follow and
  // `onFollowPersisted` already refreshed the context. Navigating buttons go.
  // Post-gate navigation, fired by `onAccessGranted` AFTER the guard modal
  // completes (follow / contact-info / lead-form). This is OUTSIDE the original
  // click gesture, so it always navigates in the SAME tab — a `window.open(…,
  // '_blank')` here is popup-blocked (returns null → nothing happens) AND, for a
  // `_blank` button, races the native anchor into a double navigation (new tab +
  // current tab). Same-tab is correct for the deliberate post-gate flow; the
  // in-gesture new-tab open for an ALREADY-granted direct click is handled by the
  // native anchor below (which keeps `_blank` inside the user gesture).
  //
  // No destination → nothing to navigate (FollowMe / `actionOnly`, where the
  // guard modal IS the action). A follow-RESTRICTED *navigating* button
  // (DigitalProduct / Music / Text / ContactCard) DOES have an `href` and must
  // proceed once the follow completes — so gate only on the missing href, NOT on
  // `guard.kind === 'follow'` (which wrongly blocked every follow-gated nav
  // button from reaching its destination).
  const activate = () => {
    if (href == null) return;
    if (href.startsWith('/')) {
      router.push(href);
    } else if (target === '_blank') {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.assign(href);
    }
  };

  // Below-tier → subscribe picker preselected at the required level
  // (RN `defaultLevel`). Invoked by the guard's subscription branch. The picker
  // is the creator-scoped `<bioContext>/subscribe` route; a soft push lets the
  // bio's `@modal/(.)subscribe` intercept overlay it as a modal.
  //
  // Carry the button's destination as `redirect_to` so that AFTER the paid-tier
  // checkout succeeds the buyer is returned to the button's `href` (now unlocked
  // by the held tier) instead of the default storefront — the same
  // return-to-origin the PDP subscription gate uses. Omitted for a button with no
  // destination (FollowMe never has a subscription gate anyway).
  //
  // A `target="_blank"` button (DigitalProduct / Music) is full-page by design —
  // and its `/products/…` route would otherwise open as a `(.)` DRAWER intercept
  // on the soft return. So make its `redirect_to` ABSOLUTE, which the checkout
  // success reads as "full document load" → the real full-page destination, no
  // drawer. In-app buttons (ChatWithMe / Community) keep a relative path → a soft
  // SPA return. (An external `_blank` href — Music — is already absolute.)
  const goToSubscribe = () => {
    if (guard.kind !== 'subscription') return;
    const redirectTo =
      href && target === '_blank' && href.startsWith('/')
        ? `${window.location.origin}${href}`
        : href;
    router.push(
      bioUrl(bioContext).subscribe({
        // `PopStoreSubscriptionLevel` (paid tiers) is a subset of the extended levels.
        defaultLevel:
          guard.level as unknown as PopStoreExtendedSubscriptionLevel,
        redirectTo,
      }),
    );
  };

  // Map the button's `CustomButtonGuard` (+ RE `skipFollowGuard` override) to
  // the shared `BioAccessGuard` descriptor. `downgradeIfTierHeld` resolves the
  // viewer-dependent tier check (a held tier downgrades a `subscription` gate to
  // `skip-follow`) in one shared place, so the guard stays viewer-independent.
  const base = { triggers, leadCapture, source };
  const accessGuard: BioAccessGuard =
    guard.kind === 'contact-info'
      ? { ...base, kind: 'contact-info' }
      : guard.kind === 'subscription'
        ? downgradeIfTierHeld(
            {
              ...base,
              kind: 'subscription',
              level: guard.level as unknown as PopStoreSubscriptionLevel,
            },
            extendedSubscriptionLevel,
          )
        : guard.kind === 'follow' && !skipFollowGuard
          ? {
              ...base,
              kind: 'follow',
              followCopy: guard.followCopy,
              // A link button's "collect contact info": anonymous viewers get
              // the guest-contact (simple lead) form instead of the follow
              // modal; logged-in non-followers still see "Follow Me".
              anonymousContactInfo: guard.anonymousContactInfo,
            }
          : skipFollowGuard
            ? // RE/booking override: following isn't meaningful; anonymous
              // still guest-contacts (a lead is captured).
              { ...base, kind: 'skip-follow' }
            : // Truly ungated (kind 'none'): proceed for everyone, no
              // guest-contact — only a matching lead form (via triggers) gates.
              { ...base, kind: 'none' };

  return (
    <AccessGuard
      storeId={storeId}
      guard={accessGuard}
      sellerProfile={sellerProfile}
      isFollowing={isFollowing}
      isSubscriptionResolved={!accessLoading}
      onFollowPersisted={onFollowPersisted}
      onSubscriptionRequired={goToSubscribe}
      onAccessGranted={activate}
    >
      {(accessGranted, requestAccess) =>
        actionOnly ? (
          // Action button: a plain `<button>`. The guard IS the action, so a
          // plain tap always runs the guard (which performs the effect and
          // fires `onAccessGranted`); already-granted taps are a no-op.
          <button
            type="button"
            aria-label={ariaLabel}
            className={className}
            onClick={() => {
              if (!accessGranted) requestAccess();
            }}
          >
            {children}
          </button>
        ) : (
          <a
            // Real href for crawlers / modified clicks; the plain click is
            // intercepted so the gate runs first.
            href={href}
            target={target}
            rel={rel}
            aria-label={ariaLabel}
            className={className}
            onClick={e => {
              // Let modified clicks (new tab / download) use the native anchor.
              if (
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey ||
                e.button !== 0
              ) {
                return;
              }
              // Access already satisfied. An INTERNAL target still has to
              // soft-navigate: the native anchor is a document load, which the
              // bio's `(.)product` intercept can't fire on, so a gated
              // digital-product button whose gate was already met opened the
              // STANDALONE PDP over the bio instead of the drawer — while the
              // ungated path (`next/link`) and the post-gate path (`activate`)
              // both drawered it. External / `_blank` targets keep the native
              // anchor: a scripted open after the click is popup-blocked.
              if (accessGranted) {
                if (
                  href != null &&
                  target !== '_blank' &&
                  href.startsWith('/')
                ) {
                  e.preventDefault();
                  activate();
                }
                return;
              }
              // Gate not yet satisfied → intercept and run the guard, which
              // shows the right modal (or redirects to subscribe). On
              // completion the guard fires `onAccessGranted` → `activate()`.
              e.preventDefault();
              requestAccess();
            }}
          >
            {children}
          </a>
        )
      }
    </AccessGuard>
  );
}
